import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, AlertTriangle, Boxes, Package, TrendingDown, Pencil } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import FilterChipBar from "@/components/FilterChipBar";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton";
import useSmartFilters from "@/hooks/useSmartFilters";

/** Compute stock health: ok | low | critical */
function stockHealth(row) {
  if (row.min_stock <= 0) return row.stock <= 0 ? "critical" : "ok";
  const ratio = row.stock / row.min_stock;
  if (ratio <= 0.5) return "critical";
  if (ratio <= 1) return "low";
  return "ok";
}

const HEALTH_STYLES = {
  ok:       { dot: "bg-emerald-500", bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  low:      { dot: "bg-amber-500",   bar: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50 border-amber-200" },
  critical: { dot: "bg-destructive", bar: "bg-destructive", text: "text-destructive", bg: "bg-red-50 border-red-200" },
};

export default function Inventory() {
  const { t, lang } = useI18n();
  const { get, set, clear } = useSmartFilters("inventory");
  const stockFilter = get("stock") || get("filter") || ""; // supports both ?stock= and ?filter=
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);            // Sprint 19: edit mode
  const empty = {
    name: "", unit: "kg", stock: 0, min_stock: 0,
    // Sprint 29.5 — VAT-aware cost engine.
    price_input_mode: "gross",
    vat_rate: 20,
    cost_gross: 0,
    cost_net: 0,
    last_supplier: "",
  };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [stats, setStats] = useState({ total: 0, low: 0, critical: 0, totalValue: 0 });
  const [statsError, setStatsError] = useState(null);

  // Live counterpart calc — always use NET internally for valuation.
  const factor = 1 + (parseFloat(form.vat_rate) || 0) / 100;
  const displayedNet   = form.price_input_mode === "net"
    ? (+form.cost_net || 0)
    : Math.round(((+form.cost_gross || 0) / (factor || 1)) * 10000) / 10000;
  const displayedGross = form.price_input_mode === "gross"
    ? (+form.cost_gross || 0)
    : Math.round(((+form.cost_net || 0) * factor) * 10000) / 10000;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inventory");
        let low = 0, critical = 0, totalValue = 0;
        data.forEach((r) => {
          const h = stockHealth(r);
          if (h === "low") low++;
          if (h === "critical") critical++;
          // Sprint 29.5 — valuation uses net unit cost.
          const unitNet = r.cost_net ?? r.cost ?? 0;
          totalValue += (r.stock || 0) * unitNet;
        });
        setStats({ total: data.length, low, critical, totalValue });
        setStatsError(null);
      } catch (e) {
        setStatsError(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
      }
    })();
  }, [refreshKey]);

  const openNew = () => { setForm(empty); setEditingId(null); setErrors({}); setOpen(true); };
  const openEdit = (row) => {
    const mode = row.price_input_mode || "gross";
    const vat  = row.vat_rate ?? 20;
    const legacyCost = row.cost ?? 0;
    const gross = row.cost_gross ?? (mode === "gross" ? legacyCost : legacyCost * (1 + vat / 100));
    const net   = row.cost_net   ?? (mode === "net"   ? legacyCost : legacyCost / (1 + vat / 100));
    setForm({
      name: row.name || "",
      unit: row.unit || "kg",
      stock: row.stock ?? 0,
      min_stock: row.min_stock ?? 0,
      price_input_mode: mode,
      vat_rate: vat,
      cost_gross: Math.round(gross * 10000) / 10000,
      cost_net:   Math.round(net * 10000) / 10000,
      last_supplier: row.last_supplier || "",
    });
    setEditingId(row.id);
    setErrors({});
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = lang === "tr" ? "İsim zorunlu" : "Name erforderlich";
    if (isNaN(parseFloat(form.stock))) e.stock = lang === "tr" ? "Sayı olmalı" : "Zahl erforderlich";
    const primary = form.price_input_mode === "net" ? form.cost_net : form.cost_gross;
    if (isNaN(parseFloat(primary))) e.cost = lang === "tr" ? "Sayı olmalı" : "Zahl erforderlich";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const submit = async () => {
    if (!validate()) return;
    try {
      const body = {
        name: form.name,
        unit: form.unit,
        stock: +form.stock,
        min_stock: +form.min_stock,
        vat_rate: +form.vat_rate,
        price_input_mode: form.price_input_mode,
        cost_gross: +form.cost_gross,
        cost_net: +form.cost_net,
        last_supplier: form.last_supplier || "",
      };
      if (editingId) {
        await api.put(`/inventory/${editingId}`, body);
      } else {
        await api.post("/inventory", body);
      }
      setOpen(false); setForm(empty); setEditingId(null); setErrors({}); setRefreshKey((k) => k + 1);
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/inventory/${id}`);
      setRefreshKey((k) => k + 1);
      toast.success(t("delete"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const columns = [
    { key: "name", label: lang === "tr" ? "Ürün" : "Artikel", className: "font-medium", render: (r) => {
      const h = stockHealth(r);
      const s = HEALTH_STYLES[h];
      return (
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${s.dot} shrink-0`} />
          <div className="min-w-0">
            <div className="truncate">{r.name}</div>
            {h !== "ok" && (
              <div className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${s.text}`}>
                {h === "critical" ? <AlertTriangle className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {h === "critical"
                  ? (lang === "tr" ? "Kritik" : "Kritisch")
                  : (lang === "tr" ? "Az stok" : "Niedrig")}
              </div>
            )}
          </div>
        </div>
      );
    }},
    { key: "stock", label: lang === "tr" ? "Stok" : "Bestand", className: "font-mono", render: (r) => {
      const h = stockHealth(r);
      const s = HEALTH_STYLES[h];
      const pct = r.min_stock > 0 ? Math.min(100, (r.stock / (r.min_stock * 2)) * 100) : 100;
      return (
        <div className="w-24 md:w-32">
          <div className="text-sm font-mono whitespace-nowrap">{r.stock} <span className="text-muted-foreground text-xs">{r.unit}</span></div>
          <div className="h-1 bg-muted rounded-full overflow-hidden mt-1 hidden sm:block">
            <div className={`h-full rounded-full transition-all ${s.bar}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    }},
    { key: "min_stock", label: lang === "tr" ? "Min" : "Min", className: "text-right font-mono text-muted-foreground w-24 hidden md:table-cell", render: (r) => `${r.min_stock} ${r.unit}` },
    { key: "cost_net", label: lang === "tr" ? "Net" : "Netto", className: "text-right font-mono w-24", render: (r) => (
      <div>
        <div>{money(r.cost_net ?? r.cost ?? 0)}</div>
        {(r.cost_gross ?? null) !== null && r.cost_gross !== (r.cost_net ?? r.cost) && (
          <div className="text-[10px] text-muted-foreground">
            {money(r.cost_gross)} {lang === "tr" ? "brüt" : "brutto"}
          </div>
        )}
      </div>
    )},
    { key: "vat_rate", label: "VAT", className: "text-right font-mono text-xs text-muted-foreground w-14 hidden md:table-cell", render: (r) => `${r.vat_rate ?? 20}%` },
    { key: "value", label: lang === "tr" ? "Değer (Net)" : "Wert (Netto)", className: "text-right font-mono font-semibold w-28 hidden md:table-cell", render: (r) => money((r.stock || 0) * (r.cost_net ?? r.cost ?? 0)) },
    { key: "actions", label: "", className: "text-right w-24", render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-inv-${r.id}`} className="h-8 w-8 rounded-lg hover:bg-primary/10">
          <Pencil className="w-4 h-4 text-primary" />
        </Button>
        <ConfirmDeleteButton
          onConfirm={() => remove(r.id)}
          itemName={r.name}
          data-testid={`del-inv-${r.id}`}
        />
      </div>
    )},
  ];

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-[1400px] mx-auto">
      <PageHeader
        title={t("inventory")}
        description={lang === "tr"
          ? "Ürün stokları, minimum seviyeler ve stok değeri"
          : "Bestand, Mindestmengen und Warenwert"}
        action={{ label: t("add"), icon: Plus, onClick: openNew, testid: "new-inventory-btn" }}
      />

      {statsError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm flex items-center gap-2"
             data-testid="inventory-error">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <span className="text-destructive">{statsError}</span>
          <Button size="sm" variant="outline" className="ml-auto h-7 rounded-md text-xs"
                  onClick={() => setRefreshKey((k) => k + 1)} data-testid="inventory-retry">
            {lang === "tr" ? "Tekrar dene" : "Erneut versuchen"}
          </Button>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatMini label={lang === "tr" ? "Toplam ürün" : "Artikel"} value={stats.total} icon={Package} tone="primary" />
        <StatMini label={lang === "tr" ? "Az stok" : "Niedrig"} value={stats.low} icon={TrendingDown} tone="amber" />
        <StatMini label={lang === "tr" ? "Kritik" : "Kritisch"} value={stats.critical} icon={AlertTriangle} tone="red" />
        <StatMini label={lang === "tr" ? "Stok değeri" : "Warenwert"} value={money(stats.totalValue)} icon={Boxes} tone="emerald" />
      </div>

      {(stats.critical > 0 || stats.low > 0) && (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${stats.critical > 0 ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${stats.critical > 0 ? "text-destructive" : "text-amber-600"}`} />
          <div className="text-sm flex-1">
            <div className="font-semibold">
              {lang === "tr" ? "Yeniden sipariş önerisi" : "Nachbestellung empfohlen"}
            </div>
            <div className="text-muted-foreground mt-0.5">
              {lang === "tr"
                ? `${stats.critical} kritik ve ${stats.low} az stok kalemi var. Aşağıdaki listeden hızlıca güncelle.`
                : `${stats.critical} kritisch und ${stats.low} niedrig. Bitte prüfen.`}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {stockFilter !== "critical" && stats.critical > 0 && (
              <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => set("stock", "critical")} data-testid="filter-critical-btn">
                {lang === "tr" ? "Kritikleri gör" : "Kritische zeigen"}
              </Button>
            )}
            {stockFilter !== "low" && stats.low > 0 && (
              <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => set("stock", "low")} data-testid="filter-low-btn">
                {lang === "tr" ? "Azları gör" : "Niedrige zeigen"}
              </Button>
            )}
          </div>
        </div>
      )}

      {stockFilter && (
        <FilterChipBar
          chips={[{
            key: "stock",
            label: stockFilter === "critical"
              ? (lang === "tr" ? "Sadece kritik stok" : "Nur kritischer Bestand")
              : (lang === "tr" ? "Sadece az stok" : "Nur niedrig"),
            onClear: () => { set("stock", null); set("filter", null); },
          }]}
          onClearAll={clear}
        />
      )}

      <DataTable
        columns={columns}
        fetcher={async () => {
          const { data } = await api.get("/inventory");
          const filtered = stockFilter === "critical"
            ? data.filter((r) => stockHealth(r) === "critical")
            : stockFilter === "low"
              ? data.filter((r) => stockHealth(r) === "low")
              : data;
          return { items: filtered, total: filtered.length, page: 1, page_size: filtered.length };
        }}
        pageSize={100}
        refreshKey={`${refreshKey}-${stockFilter}`}
        rowTestId="inv-row"
        searchPlaceholder={lang === "tr" ? "Ürün ara…" : "Suchen…"}
        emptyProps={{ icon: Boxes, title: lang === "tr" ? "Stok kaydı yok" : "Kein Bestand", description: lang === "tr" ? "İlk ürünü ekleyerek başla." : "Erstes Produkt hinzufügen.", action: { label: t("add"), icon: Plus, onClick: openNew } }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="!max-w-md !w-full
                     !left-0 !top-auto !bottom-0 !right-auto !translate-x-0 !translate-y-0
                     !h-[100dvh] !max-h-[100dvh]
                     sm:!left-[50%] sm:!top-[50%] sm:!bottom-auto sm:!right-auto
                     sm:!translate-x-[-50%] sm:!translate-y-[-50%]
                     sm:!h-auto sm:!max-h-[92vh] sm:!w-[calc(100vw-2rem)]
                     !p-0 !gap-0 flex flex-col rounded-none sm:rounded-lg overflow-hidden"
          data-testid="inv-dialog"
        >
          <DialogHeader className="flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4 border-b bg-background">
            <DialogTitle className="font-display tracking-tight" data-testid="inv-dialog-title">
              {editingId ? (lang === "tr" ? "Ürünü Düzenle" : "Artikel bearbeiten") : t("add")}
            </DialogTitle>
          </DialogHeader>
          <div
            className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 space-y-4"
            style={{ WebkitOverflowScrolling: "touch" }}
            data-testid="inv-dialog-body"
          >
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "tr" ? "İsim" : "Name"}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" className="mt-1.5 h-10 rounded-lg" />
              {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "tr" ? "Birim" : "Einheit"}</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "tr" ? "Stok" : "Bestand"}</Label>
                <Input type="number" step="0.01" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1.5 h-10 rounded-lg font-mono" data-testid="inv-stock" />
                {errors.stock && <p className="text-xs text-destructive mt-1">{errors.stock}</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Min</Label>
                <Input type="number" step="0.01" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="mt-1.5 h-10 rounded-lg font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "tr" ? "KDV" : "MwSt"}</Label>
                <select
                  value={form.vat_rate}
                  onChange={(e) => setForm({ ...form, vat_rate: +e.target.value })}
                  className="mt-1.5 w-full h-10 rounded-lg border bg-background px-3 text-sm font-mono"
                  data-testid="inv-vat"
                >
                  <option value={0}>0%</option>
                  <option value={10}>10%</option>
                  <option value={13}>13%</option>
                  <option value={20}>20%</option>
                </select>
              </div>
            </div>

            <div className="rounded-xl border bg-gradient-to-br from-primary/5 to-transparent p-3 space-y-3"
                 data-testid="inv-cost-panel">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                {lang === "tr" ? "Birim Maliyet (VAT-farkındalı)" : "Stückkosten (MwSt-bewusst)"}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, price_input_mode: "gross" })}
                  data-testid="inv-mode-gross"
                  data-active={form.price_input_mode === "gross"}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-all
                              ${form.price_input_mode === "gross"
                                ? "border-primary bg-primary text-primary-foreground"
                                : "hover:border-primary/60"}`}
                >
                  {lang === "tr" ? "Brüt gir" : "Brutto"}
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, price_input_mode: "net" })}
                  data-testid="inv-mode-net"
                  data-active={form.price_input_mode === "net"}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-all
                              ${form.price_input_mode === "net"
                                ? "border-primary bg-primary text-primary-foreground"
                                : "hover:border-primary/60"}`}
                >
                  {lang === "tr" ? "Net gir" : "Netto"}
                </button>
              </div>
              {form.price_input_mode === "gross" ? (
                <div>
                  <Label className="text-xs">{lang === "tr" ? "Brüt (KDV dahil) €" : "Brutto (inkl. MwSt) €"}</Label>
                  <Input
                    type="number" step="0.01" value={form.cost_gross}
                    onChange={(e) => setForm({ ...form, cost_gross: e.target.value })}
                    className="mt-1.5 h-10 rounded-lg font-mono"
                    data-testid="inv-cost-gross"
                  />
                  <div className="mt-1 text-[11px] text-muted-foreground font-mono" data-testid="inv-calc-net">
                    → {lang === "tr" ? "Net" : "Netto"}: {money(displayedNet)}
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-xs">{lang === "tr" ? "Net (KDV hariç) €" : "Netto (exkl. MwSt) €"}</Label>
                  <Input
                    type="number" step="0.01" value={form.cost_net}
                    onChange={(e) => setForm({ ...form, cost_net: e.target.value })}
                    className="mt-1.5 h-10 rounded-lg font-mono"
                    data-testid="inv-cost-net"
                  />
                  <div className="mt-1 text-[11px] text-muted-foreground font-mono" data-testid="inv-calc-gross">
                    → {lang === "tr" ? "Brüt" : "Brutto"}: {money(displayedGross)}
                  </div>
                </div>
              )}
              {errors.cost && <p className="text-xs text-destructive">{errors.cost}</p>}
              <div className="text-[10px] text-muted-foreground leading-relaxed">
                {lang === "tr"
                  ? "Stok değeri ve maliyet hesaplarında her zaman NET kullanılır."
                  : "Warenwert und Kostenrechnung nutzen immer den Netto-Preis."}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                {lang === "tr" ? "Son tedarikçi" : "Letzter Lieferant"}
              </Label>
              <Input
                value={form.last_supplier || ""}
                onChange={(e) => setForm({ ...form, last_supplier: e.target.value })}
                className="mt-1.5 h-10 rounded-lg"
                data-testid="inv-last-supplier"
                placeholder="Metro / Kröswang / …"
              />
            </div>
          </div>
          <div
            className="flex-shrink-0 flex justify-end gap-2 px-4 py-3 sm:px-6 border-t bg-background/95 backdrop-blur
                       [padding-bottom:calc(env(safe-area-inset-bottom)+0.75rem)]"
            data-testid="inv-dialog-footer"
          >
            <Button variant="outline" onClick={() => setOpen(false)} className="min-h-[44px] rounded-lg" data-testid="cancel-inv">
              {t("cancel")}
            </Button>
            <Button onClick={submit} data-testid="save-inv" className="min-h-[44px] rounded-lg shadow-elev-2">
              {editingId
                ? (lang === "tr" ? "Değişiklikleri Kaydet" : "Änderungen speichern")
                : t("save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatMini({ label, value, icon: Icon, tone }) {
  const toneClass = {
    primary: "bg-primary/10 text-primary",
    amber: "bg-amber-500/10 text-amber-600",
    red: "bg-destructive/10 text-destructive",
    emerald: "bg-emerald-500/10 text-emerald-600",
  }[tone] || "bg-muted";
  return (
    <div className="border rounded-xl p-4 bg-background shadow-elev-1 card-hover">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground truncate">{label}</span>
        {Icon && <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${toneClass}`}><Icon className="w-3.5 h-3.5" /></div>}
      </div>
      <div className="font-mono text-xl md:text-2xl font-semibold tracking-[-0.02em]">{value}</div>
    </div>
  );
}
